import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export interface ContextMenuItemDef {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  separator?: boolean;
  /**
   * Optional `data-testid` on the rendered item. Memory rule
   * `feedback_data_testid`: every interactive surface needs a stable
   * id so Playwright can drive it. Pass a value from `testIds.*`.
   */
  testId?: string;
}

interface ContextMenuProps {
  items: ContextMenuItemDef[];
  position: { x: number; y: number };
  onClose: () => void;
}

const FONT = "var(--xp-font-body)";
const MENU_W = 200;
const ITEM_H = 26;

export function SharePointContextMenu({ items, position, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [onClose]);

  const contentH = items.reduce((h, it) => h + (it.separator ? 7 : ITEM_H), 0) + 6;
  const adjustedX = position.x + MENU_W > window.innerWidth ? position.x - MENU_W : position.x;
  const adjustedY = position.y + contentH > window.innerHeight ? position.y - contentH : position.y;

  const menu = (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: adjustedY,
        left: adjustedX,
        width: MENU_W,
        background: "#ece9d8",
        borderStyle: "solid",
        borderWidth: 2,
        boxShadow: "2px 2px 0 #000",
        zIndex: 3000,
        padding: "3px 0",
      }}
    >
      {items.map((item, i) =>
        item.separator ? (
          <div
            key={`sep-${i}`}
            style={{
              height: 1,
              background: "#808080",
              borderBottom: "1px solid #dfdfdf",
              margin: "2px 4px",
            }}
          />
        ) : (
          <div
            key={`${item.label}-${i}`}
            data-testid={item.testId}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (!item.disabled) {
                item.onClick();
                onClose();
              }
            }}
            onMouseEnter={(e) => {
              if (!item.disabled) {
                (e.currentTarget as HTMLDivElement).style.background = "var(--xp-selection)";
                (e.currentTarget as HTMLDivElement).style.color = "white";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.background = "";
              (e.currentTarget as HTMLDivElement).style.color = item.disabled ? "#808080" : "#000";
            }}
            style={{
              display: "flex",
              alignItems: "center",
              height: ITEM_H,
              padding: "0 20px",
              fontFamily: FONT,
              fontSize: 12,
              color: item.disabled ? "#808080" : "#000",
              cursor: item.disabled ? "default" : "pointer",
              userSelect: "none",
            }}
          >
            {item.label}
          </div>
        )
      )}
    </div>
  );

  return createPortal(menu, document.body);
}
